/**
 * The organization's plan: what it includes, the others side by side, and paying through Stripe.
 * The plan changes only when Stripe confirms the payment; this screen only starts the checkout.
 *
 * Every plan uses the person's own Claude or API key: Vephus sells the engineering around the
 * model — what to send, when, how to verify it — never tokens.
 */

import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Users, Check, CreditCard, Minus } from 'lucide-react'
import { api } from '../lib/api'
import { useI18n } from '../lib/i18n'
import { openExternal } from '../lib/desktop'
import { Badge, ErrorBanner, Skeleton } from '../components/primitives'
import type { Entitlements, PlanOffer } from '../lib/types'

const ROWS: Array<{ key: Exclude<keyof Entitlements, 'plan'>; kind: 'days' | 'bool' | 'count' }> = [
  { key: 'telemetryHistoryDays', kind: 'days' },
  { key: 'latestAudits', kind: 'bool' },
  { key: 'maxLoopRounds', kind: 'count' },
  { key: 'maxActiveSchedules', kind: 'count' },
  // Work done together — a teammate's agent and the builder/reviewer cycle — is the per-seat plan's (docs/refactor/40).
  { key: 'teamWork', kind: 'bool' },
  { key: 'managerView', kind: 'bool' },
  { key: 'maxCustomPlugins', kind: 'count' },
  { key: 'promptAnalysis', kind: 'bool' },
  // The full log level (the owner, 15/10): partial replies and whole commands in a session's Detalhes.
  { key: 'fullAgentLog', kind: 'bool' },
  // The characters' Pro items (docs/refactor/47).
  { key: 'premiumLooks', kind: 'bool' },
  // Quality gates: an entitlement in Plans.cs, not shown until the screen and the rule exist (docs/refactor/28).
]
const UNLIMITED = 2147483647

export default function Billing() {
  const { t } = useI18n()
  const queryClient = useQueryClient()
  const { data: state, isLoading, error } = useQuery({ queryKey: ['billing'], queryFn: api.billing.state })
  const [seats, setSeats] = useState(1)
  const [problem, setProblem] = useState<string | null>(null)
  const paid = new URLSearchParams(window.location.search).get('paid') === '1'

  // Back from Stripe: the webhook may take a moment, so look again for a little while.
  useEffect(() => {
    if (!paid) return
    const timer = setInterval(() => void queryClient.invalidateQueries({ queryKey: ['billing'] }), 3000)
    const stop = setTimeout(() => clearInterval(timer), 30_000)
    return () => {
      clearInterval(timer)
      clearTimeout(stop)
    }
  }, [paid, queryClient])

  const go = (url: string) => void openExternal(url).catch(() => (window.location.href = url))
  const checkout = useMutation({
    mutationFn: (offer: PlanOffer) => api.billing.checkout(offer.code, offer.perSeat ? seats : undefined),
    onSuccess: ({ url }) => go(url),
    onError: () => setProblem(t('plans.error')),
  })
  const portal = useMutation({ mutationFn: api.billing.portal, onSuccess: ({ url }) => go(url), onError: () => setProblem(t('plans.error')) })
  const trial = useMutation({
    mutationFn: api.billing.trial,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['billing'] }),
    onError: () => setProblem(t('plans.error')),
  })

  if (isLoading) return <Skeleton height={240} />
  if (error || !state) return <ErrorBanner message={t('error.generic')} />

  const cell = (value: number | boolean, kind: 'days' | 'bool' | 'count') => {
    if (kind === 'bool') return value ? <Check size={15} className="plans__yes" /> : <Minus size={14} className="plans__no" />
    const n = value as number
    if (n >= UNLIMITED) return t('plans.unlimited')
    return kind === 'days' ? t('plans.days', { count: n }) : String(n)
  }

  return (
    <div className="stack">
      {paid && <div className="notice small">{t('plans.thanks')}</div>}
      <div className="row row--between row--wrap">
        <div className="stack" style={{ gap: 2 }}>
          <span className="small muted">{t('plans.current')}</span>
          <strong className="plans__current">{state.plan}</strong>
          {state.status === 'past_due' && <Badge tone="warn">{t('plans.pastDue')}</Badge>}
          {state.renewsAt && <span className="tiny muted">{t('plans.renews', { at: new Date(state.renewsAt).toLocaleDateString() })}</span>}
        </div>
        <div className="row row--tight row--wrap">
          {/* The one Team trial (41 §4.3): working together and the manager's view, fourteen days, no card. */}
          {state.canManageBilling && state.teamTrialAvailable && (
            <button type="button" className="btn btn--primary" onClick={() => trial.mutate()} disabled={trial.isPending}>
              <Users size={14} /> {t('plans.trial')}
            </button>
          )}
          {state.canManageBilling && state.plan !== 'Free' && (
            <button type="button" className="btn" onClick={() => portal.mutate()} disabled={portal.isPending}>
              <CreditCard size={14} /> {t('plans.manage')}
            </button>
          )}
        </div>
      </div>
      {state.teamTrialEndsAt && state.plan !== 'Team' && (
        new Date(state.teamTrialEndsAt) > new Date() ? (
          <div className="notice small">{t('plans.trialOn', { at: new Date(state.teamTrialEndsAt).toLocaleDateString() })}</div>
        ) : (
          <div className="notice small">{t('plans.trialOver')}</div>
        )
      )}

      {!state.billingAvailable && <div className="notice notice--warn small">{t('plans.notSetUp')}</div>}
      {problem && <ErrorBanner message={problem} />}

      <div className="plans">
        <table>
          <thead>
            <tr>
              <th />
              {state.offers.map((offer) => (
                <th key={offer.code} className={offer.plan === state.plan ? 'is-current' : undefined}>
                  <div className="stack" style={{ gap: 4, alignItems: 'center' }}>
                    <strong>{offer.plan}</strong>
                    <span className="small">{offer.monthlyPrice === 0 ? t('plans.free') : t(offer.perSeat ? 'plans.pricePerSeat' : 'plans.price', { price: offer.monthlyPrice })}</span>
                    {offer.plan === state.plan ? (
                      <Badge tone="ok">{t('plans.yours')}</Badge>
                    ) : offer.plan !== 'Free' && state.canManageBilling ? (
                      <>
                        {offer.perSeat && (
                          <label className="tiny row row--tight">
                            {t('plans.seats')}
                            <input className="input" type="number" min={1} max={500} value={seats} style={{ width: 64 }} onChange={(e) => setSeats(Math.max(1, Number(e.target.value) || 1))} />
                          </label>
                        )}
                        <button type="button" className="btn btn--primary btn--sm" onClick={() => checkout.mutate(offer)} disabled={checkout.isPending || !state.billingAvailable}>
                          {t('plans.choose', { plan: offer.plan })}
                        </button>
                      </>
                    ) : null}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ROWS.map((row) => (
              <tr key={row.key}>
                <th scope="row" className="small">{t(`plans.row.${row.key}`)}</th>
                {state.offers.map((offer) => (
                  <td key={offer.code} className={offer.plan === state.plan ? 'is-current' : undefined}>{cell(offer.includes[row.key], row.kind)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="tiny muted" style={{ margin: 0 }}>{t('plans.byok')}</p>
    </div>
  )
}
